import { useCallback, useEffect, useMemo, useState } from 'react';
import { isTransientOverviewApiError, overviewApi } from '../services/overviewApi';
import { ensureUiScope } from '../services/api';
import { mapOverviewControlSummaryResponse } from '../domain/overview/overviewControlSummary';
import {
  buildCameraPatternLinkContext,
  type CameraPatternLinkContext,
} from '../domain/overview/cameraPatternContext';
import { readOverviewSnapshotCache } from '../utils/overviewSnapshotCache';

export type LiveViewPatternContextState = {
  byCamera: Record<string, CameraPatternLinkContext>;
  loaded: boolean;
  error: string | null;
  refresh: () => void;
};

/** Match Overview snapshot params so inflight control-summary requests dedupe. */
const CONTROL_SUMMARY_PARAMS = { hours: 168, top_limit: 10 } as const;

function contextFromCache(siteId: string): Record<string, CameraPatternLinkContext> | null {
  const orgId = ensureUiScope().orgId;
  if (!orgId || !siteId) return null;
  const cached = readOverviewSnapshotCache(orgId, siteId);
  const patterns = cached?.controlSummary?.topPatterns;
  if (!patterns?.length) return null;
  return buildCameraPatternLinkContext(patterns);
}

async function fetchControlSummaryWithRetry() {
  try {
    return await overviewApi.getControlSummary({ ...CONTROL_SUMMARY_PARAMS });
  } catch (error) {
    if (!isTransientOverviewApiError(error)) throw error;
    await new Promise((resolve) => window.setTimeout(resolve, 2500));
    return overviewApi.getControlSummary({ ...CONTROL_SUMMARY_PARAMS });
  }
}

export function useLiveViewPatternContext(siteId: string, enabled = true): LiveViewPatternContextState {
  const [byCamera, setByCamera] = useState<Record<string, CameraPatternLinkContext>>({});
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(() => {
    if (!enabled || !siteId) {
      setByCamera({});
      setLoaded(false);
      setError(siteId ? null : 'Select org/site scope to load linked recurring risks.');
      return;
    }

    const cached = contextFromCache(siteId);
    if (cached) {
      setByCamera(cached);
      setLoaded(true);
      setError(null);
    } else {
      setLoaded(false);
      setError(null);
    }

    void (async () => {
      try {
        const response = await fetchControlSummaryWithRetry();
        const summary = mapOverviewControlSummaryResponse(response);
        if (!summary.loaded) {
          if (!cached) setByCamera({});
          setError(summary.error || 'Linked recurring-risk context unavailable.');
          return;
        }
        setByCamera(buildCameraPatternLinkContext(summary.topPatterns));
        setError(null);
      } catch {
        if (!cached) setByCamera({});
        setError('Linked recurring-risk context unavailable.');
      } finally {
        setLoaded(true);
      }
    })();
  }, [enabled, siteId]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  return useMemo(
    () => ({
      byCamera,
      loaded,
      error,
      refresh,
    }),
    [byCamera, error, loaded, refresh],
  );
}
